<script lang="ts" setup>
  import type { AutocompleteTypes, TextFieldTypes } from '@ionic/core'
  import type { InputCustomEvent } from '@ionic/core/dist/types/components/input/input-interface'
  import GInput from '~/components/common/GInput/GInput.vue'

  // Validation occurs:
  // - Validate on Change/Blur initially (when the user leaves the control), let’s call this lazy mode.
  // - If the field is invalid, switch the validation to validate on input (when the user types), let’s call this aggressive mode.
  // - If the field is valid, go back to “lazy” mode, otherwise, be “aggressive”.
  // See https://vee-validate.logaretm.com/v4/guide/composition-api/custom-inputs#handling-events

  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      label: string
      inputMode?: 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url'
      maskPattern?: 'alpha' | 'alphaNumeric' | 'numeric' | 'name'
      maxLength?: number
      placeholder?: string
      name: string
      clearInput?: boolean
      hideLabel?: boolean
      type?: TextFieldTypes
      autocapitalize?: 'off' | 'words' | 'characters' | 'sentences'
      autocomplete?: AutocompleteTypes
    }>(),
    {
      disabled: false,
      inputMode: 'text',
      maskPattern: undefined,
      maxLength: 255,
      placeholder: undefined,
      clearInput: false,
      hideLabel: false,
      type: 'text',
      autocapitalize: 'off',
      autocomplete: 'off'
    }
  )

  const emits = defineEmits<{
    (event: 'blur'): void
    (event: 'change', value?: string | number | null): void
    (event: 'focus'): void
    (event: 'input', value?: string | number | null): void
  }>()

  const inputElement = ref<InstanceType<typeof GInput> | null>(null)
  defineExpose({ inputElement })

  const { value, errorMessage, handleBlur, handleChange } = useField<string | number | null | undefined>(
    () => props.name
  )

  function onBlur(event?: InputCustomEvent<FocusEvent>) {
    emits('blur')
    handleBlur(event, true)
  }

  function onChange(event?: InputCustomEvent) {
    emits('change', event?.target.value)
    handleChange(event?.target.value)
  }

  function onInput(event?: InputCustomEvent) {
    emits('input', event?.target.value)
    handleChange(event?.target.value, !!errorMessage.value)
  }
</script>

<template>
  <GInput
    ref="inputElement"
    :autocapitalize="autocapitalize"
    :autocomplete="autocomplete"
    :clear-input="clearInput"
    :disabled="disabled"
    :error-message="errorMessage"
    :hide-label="hideLabel"
    :inputmode="inputMode"
    :label="label"
    :mask-pattern="maskPattern"
    :max-length="maxLength"
    :name="name"
    :placeholder="placeholder"
    :type="type"
    :value="value"
    @blur="onBlur"
    @change="onChange"
    @focus="$emit('focus')"
    @input="onInput"
  />
</template>
